import React from 'react';
import styled, { css } from 'styled-components';
import { LFC } from '../../Legacy';
import { Icon } from '../../concepts/resources/Icon';
import { Colors, remInPx } from './../../ui';

const sizeToPx = (p: { size?: number, unit?: string }) =>
    p.unit !== 'px' ? remInPx(p.size ?? 2.5) : p.size ?? 40;

const Frame = styled.div<{ border?: boolean, clickable?: boolean, size?: number, unit?: string }>`
    display: inline-block;
    position: relative;
    width: ${sizeToPx}px;
    min-width: ${sizeToPx}px;
    height: ${sizeToPx}px;
    min-height: ${sizeToPx}px;
    margin: 1px;
    line-height: ${p => sizeToPx(p) + 1}px;
    font-size: ${p => sizeToPx(p) * .45}px;
    font-weight: 450;
    text-align: center;
    border-radius: 50%;
    color: white;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 1px 0 white inset, 0 0 1px 0 white;
    user-select: none;
    background-size: cover;

    ${p => (!p.border && !p.clickable) ? '' : css`
        &:after {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            content: '';
            border: solid .2rem rgba(0, 0, 0, 0.1);
            border-radius: 50%;
        }
    `}

    ${p => !p.clickable ? '' : css`

        &:hover:after { 
            cursor: pointer;
            border: solid .2rem rgba(0, 0, 0, 0.3);
        }

        &:active:after {
            background: rgba(0, 0, 0, 0.3);
        }
    `}

    &.--offline {
        opacity: 0.3;
    }

    svg, img {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 100%;
        height: 100%;
        transform: translate(-50%, -50%);
    }
`;


const StatusFrame = styled.div`
    position: absolute;
    bottom: -.125rem;
    right: -.125rem;
`;

const IndicatorOnline = styled.div`
    width: 0.75rem;
    height: 0.75rem;
    background: ${Colors.positive};
    border-radius: 50%;
    border: solid .125rem white;
`;

const IndicatorBoard = () => {
    const style = {
        position: 'relative' as const,
        left: '.125rem',
        top: '.125rem',
        width: '1rem',
        height: '0.9375rem',
        zIndex: 100,
    };
    return <div style={style}>
        <Icon src="deqo://icons/dash/board-indicator" />
    </div>;
};

const CounterStatus: LFC<{ status: string }> = ({ status, children }) => {
    return <StatusFrame>
        {children}
        {status === 'online' && <IndicatorOnline />}
        {status === 'board' && <IndicatorBoard />}
        {/* {status === 'offline' && <CounterIndicatorOffline />} */}
    </StatusFrame>;
};

export interface CounterProps {
    clickable?: boolean;
    size?: number;
    unit?: string;
    color?: string;
    photo?: string;
    status?: string;
    style?: any;
}

const Base: LFC<CounterProps> = (props) => {
    const { children, color, photo, status, clickable, size, unit, style } = props;

    const iconPhoto = photo?.startsWith('deqo://icon');

    const styling = {
        ...style,
        backgroundImage: (!!photo && photo.startsWith('http')) ? `url(${photo})` : '',
        backgroundColor: color,
    };

    return <Frame {...{ border: iconPhoto, clickable, size, unit }} style={styling}>
        {!photo && children}
        {status && <CounterStatus status={status} />}
        {iconPhoto && <Icon src={photo} style={{ transform: 'translate(-50%, -50%) scale(0.75)' }} />}
    </Frame>;
};

export const Counter = Object.assign(Base, { Frame });
